import React from 'react';

import PropTypes from 'prop-types';

import {
  stylePropType,
  StylableComponent,
  variantSwitch,
} from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import { BADGE_SIZES, BADGE_COLORS } from '../consts';
import { Size } from '../types';

/**
 * Dot Badge.
 */
type DotBadgeProps = StylableComponent<HTMLDivElement> & {
  size?: Size;
  color?: string;
};

const DotBadge = React.forwardRef<HTMLDivElement, DotBadgeProps>(
  (
    {
      style,
      className,
      size = BADGE_SIZES.MEDIUM,
      color = BADGE_COLORS.DEFAULT,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            backgroundColor: color,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          variantSwitch({
            statement: size,
            variants: {
              [BADGE_SIZES.SMALL]: {
                minHeight: 8,
                minWidth: 8,
                height: 8,
                width: 8,
                maxHeight: 8,
                maxWidth: 8,
                borderRadius: 4,
              },
              [BADGE_SIZES.MEDIUM]: {
                minHeight: 16,
                minWidth: 16,
                height: 16,
                width: 16,
                maxHeight: 16,
                maxWidth: 16,
                borderRadius: 8,
              },
            },
          }),
          style,
        ]}
        {...props}
      >
        {size === BADGE_SIZES.MEDIUM && (
          <div
            css={[
              {
                backgroundColor: theme.colors.white,
                minHeight: 6,
                minWidth: 6,
                height: 6,
                width: 6,
                maxHeight: 6,
                maxWidth: 6,
                borderRadius: 3,
              },
            ]}
          />
        )}
      </div>
    );
  }
);

DotBadge.displayName = 'DotBadge';

DotBadge.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Badge size, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(BADGE_SIZES)),
  /** Color to apply on the background of the Badge */
  color: PropTypes.string,
};

export default DotBadge;
